/**
 * Importación desde planilla, con staging y curación manual -- NUNCA
 * publica un RAT. Portado del proyecto original (rama `main`, commit
 * 5979987, `docs/Fase4b4_Importacion_Seguimiento.md`): lee CUALQUIER
 * .xlsx/.csv (SheetJS, todas las hojas), detecta y deja editar la
 * correspondencia de columnas, resuelve la unidad de cada fila por nombre
 * (editable por fila) y arma una vista previa incluir/excluir antes de
 * confirmar. Ver src/import.ts en el backend para la diferencia deliberada
 * con el original (sin tablas de staging -- el estado de React hace ese
 * papel antes del commit).
 *
 * Mismo permiso que "+ Nueva actividad" para el tipo "Actividades"
 * (activity.create); "Contactos de seguimiento" queda igual de restringido
 * que el resto de /seguimiento (institucional-solo, ver tracking.ts).
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchColumnMappings, fetchInstitutionalDashboard, importRowsApi, type ImportActivityRowInput, type ImportContactRowInput, type ImportKind, type ImportRowError, type ImportRowResult } from '../lib/queries';
import { buildStagedRows, guessMapping, IMPORT_FIELDS, parseSpreadsheet, type ParsedSheet, type StagedRow, type UnitOption } from '../lib/importApi';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/ui';

const ERROR_LABEL: Record<ImportRowError, string> = {
  missing_title: 'Falta el título',
  missing_full_name: 'Falta el nombre',
  missing_unit: 'Falta la unidad',
  forbidden: 'Sin permiso para crear en esa unidad',
};

export function Import() {
  const { authz } = useAuth();
  const instDash = useQuery({
    queryKey: ['dash', 'inst', 'units-for-import'],
    queryFn: fetchInstitutionalDashboard,
    enabled: authz.institutional,
  });
  const units: UnitOption[] = useMemo(() => {
    const own = authz.units.map((u) => ({ id: u.unit_id, name: u.name }));
    const all = instDash.data?.por_unidad.map((u) => ({ id: u.unit_id, name: u.name })) ?? [];
    const seen = new Set(own.map((u) => u.id));
    return [...own, ...all.filter((u) => !seen.has(u.id))];
  }, [authz.units, instDash.data]);

  const [kind, setKind] = useState<ImportKind>('activity_seed');
  const [sheets, setSheets] = useState<ParsedSheet[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<StagedRow[]>([]);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [results, setResults] = useState<ImportRowResult[] | null>(null);

  const sheet = sheets[sheetIndex] ?? null;
  const fields = IMPORT_FIELDS[kind];

  async function onFile(file: File) {
    setParseErr(null);
    setResults(null);
    try {
      const parsed = await parseSpreadsheet(file);
      if (parsed.length === 0) throw new Error('El archivo no tiene filas en ninguna hoja.');
      setSheets(parsed);
      setSheetIndex(0);
      applySheet(parsed[0]!, kind);
    } catch (e) {
      setParseErr(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
      setSheets([]);
      setRows([]);
    }
  }

  // Evita que una correspondencia aprendida que llega tarde (red lenta)
  // pise una hoja/tipo/archivo distinto que el usuario ya seleccionó mientras
  // se esperaba la respuesta.
  const applyTokenRef = useRef(0);

  function applySheet(s: ParsedSheet, k: ImportKind) {
    const token = ++applyTokenRef.current;
    const guessed = guessMapping(s.headers, k);
    setMapping(guessed);
    setRows(buildStagedRows(s, guessed, units));
    // Correspondencia aprendida de lotes anteriores (ver getColumnMappings()
    // en el backend) -- se superpone al adivinado por palabras clave, solo
    // para columnas que de verdad existen en este archivo.
    fetchColumnMappings(k)
      .then((learned) => {
        if (applyTokenRef.current !== token) return;
        const merged = { ...guessed };
        for (const h of s.headers) if (learned[h]) merged[h] = learned[h];
        setMapping(merged);
        setRows(buildStagedRows(s, merged, units));
      })
      .catch(() => {});
  }

  function setColMap(col: string, field: string) {
    const m = { ...mapping, [col]: field };
    setMapping(m);
    if (sheet) setRows(buildStagedRows(sheet, m, units));
  }

  const importMut = useMutation({
    mutationFn: () => {
      const included = rows.filter((r) => r.include && r.resolved_unit_id);
      if (kind === 'activity_seed') {
        const payload: ImportActivityRowInput[] = included.map((r) => ({
          unit_id: r.resolved_unit_id!,
          title: r.mapped.title ?? '',
          description: r.mapped.description || undefined,
        }));
        return importRowsApi('activity_seed', payload, mapping);
      }
      const payload: ImportContactRowInput[] = included.map((r) => ({
        unit_id: r.resolved_unit_id!,
        full_name: r.mapped.full_name ?? '',
        position: r.mapped.position || undefined,
        email: r.mapped.email || undefined,
        phone: r.mapped.phone || undefined,
        notes: r.mapped.notes || undefined,
      }));
      return importRowsApi('contact', payload, mapping);
    },
    onSuccess: (r) => {
      setResults(r);
      setRows([]);
      setSheets([]);
    },
  });

  const okRows = useMemo(() => rows.filter((r) => r.include && r.resolved_unit_id).length, [rows]);
  const unresolved = useMemo(() => rows.filter((r) => r.include && !r.resolved_unit_id).length, [rows]);
  const createdCount = (results ?? []).filter((r) => r.ok).length;

  return (
    <>
      <PageHeader
        eyebrow="Actividades de tratamiento"
        title="Importar desde planilla"
        sub="Crea contactos de seguimiento o BORRADORES de actividad para curación. Nunca publica un RAT."
      />

      <div className="callout" style={{ marginBottom: 16, fontSize: 12.5 }}>
        La importación de actividades genera <b>borradores en estado BORRADOR</b>. Cada uno debe
        completarse y enviarse a revisión con el asistente. Suba cualquier <code>.xlsx</code>/<code>.csv</code> --
        se detectan sus columnas y se puede corregir la correspondencia antes de confirmar.
      </div>

      {results && (
        <div className="callout" style={{ marginBottom: 16 }}>
          Importación completada: <b>{createdCount}</b> de <b>{results.length}</b> fila(s) creada(s).{' '}
          <Link to={kind === 'contact' ? '/seguimiento' : '/actividades'}>Ver resultado →</Link>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__b" style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Tipo de datos</span>
            <select
              value={kind}
              onChange={(e) => {
                const k = e.target.value as ImportKind;
                setKind(k);
                setResults(null);
                if (sheet) applySheet(sheet, k);
              }}
            >
              <option value="activity_seed">Actividades (a borrador)</option>
              <option value="contact">Contactos de seguimiento</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Archivo (.xlsx / .csv)</span>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          </label>
          {sheets.length > 1 && (
            <label className="field" style={{ marginBottom: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Hoja</span>
              <select
                value={sheetIndex}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  setSheetIndex(idx);
                  applySheet(sheets[idx]!, kind);
                }}
              >
                {sheets.map((s, i) => (
                  <option key={s.sheet} value={i}>
                    {s.sheet} ({s.rows.length} filas)
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {parseErr && <div className="callout callout--crit" style={{ margin: '0 16px 16px' }}>{parseErr}</div>}
      </div>

      {sheet && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__h"><h3 style={{ fontSize: 15 }}>Correspondencia de columnas</h3></div>
            <div className="card__b" style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))' }}>
              {sheet.headers.map((h) => (
                <label key={h} style={{ fontSize: 12 }}>
                  <span className="mono" style={{ display: 'block', marginBottom: 3 }}>{h}</span>
                  <select value={mapping[h] ?? ''} onChange={(e) => setColMap(h, e.target.value)} style={{ width: '100%' }}>
                    <option value="">— ignorar —</option>
                    {fields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                        {f.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <span className="pill pill--success">{okRows} lista{okRows === 1 ? '' : 's'}</span>
            {unresolved > 0 && <span className="pill pill--warning">{unresolved} sin unidad resuelta</span>}
            <span style={{ flex: 1 }} />
            <button className="btn btn--primary" disabled={okRows === 0 || importMut.isPending} onClick={() => importMut.mutate()}>
              {importMut.isPending ? 'Importando…' : `Importar ${okRows} fila(s)`}
            </button>
          </div>
          {importMut.error && (
            <div className="callout callout--crit" style={{ marginBottom: 12 }}>
              {(importMut.error as Error).message}
            </div>
          )}

          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Incluir</th>
                  {fields.filter((f) => f.key !== 'unidad').map((f) => (
                    <th key={f.key}>{f.label}</th>
                  ))}
                  <th>Unidad resuelta</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={!r.include ? { opacity: 0.4 } : undefined}>
                    <td className="mono">{r.source_row_no}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={r.include}
                        style={{ width: 'auto' }}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)))}
                      />
                    </td>
                    {fields.filter((f) => f.key !== 'unidad').map((f) => (
                      <td key={f.key} style={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.mapped[f.key] || <span className="muted">—</span>}
                      </td>
                    ))}
                    <td>
                      <select
                        value={r.resolved_unit_id ?? ''}
                        style={{ borderColor: r.include && !r.resolved_unit_id ? 'var(--critical)' : undefined, maxWidth: 200 }}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, resolved_unit_id: e.target.value || null } : x)))}
                      >
                        <option value="">— sin resolver —</option>
                        {units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

